import { registerHeaderLayout } from '../header-layout/header-layout.js';

function headingId(heading, index) {
    if (heading.id) return heading.id;
    const slug = heading.textContent
        .trim()
        .toLocaleLowerCase('de')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/gu, '')
        .replace(/[^a-z0-9]+/gu, '-')
        .replace(/^-|-$/gu, '')
        .slice(0, 54);
    heading.id = `abschnitt-${slug || 'thema'}-${index + 1}`;
    return heading.id;
}

function scrollToHeading(heading, behavior) {
    const scrollMargin = Number.parseFloat(getComputedStyle(heading).scrollMarginBlockStart) || 0;
    const top = Math.max(0, heading.getBoundingClientRect().top + window.scrollY - scrollMargin);
    window.scrollTo({ top, behavior });
}

export function initContentNavigation(navigation) {
    const selector = navigation.dataset.contentTarget;
    if (!selector) return;

    const target = document.querySelector(selector);
    const list = navigation.querySelector('[data-content-navigation-list]');
    const toggle = navigation.querySelector('[data-content-navigation-toggle]');
    const headings = target
        ? [...target.querySelectorAll(navigation.dataset.contentHeadings || 'h2')]
        : [];
    if (!list || headings.length < 3) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const links = headings.map((heading, index) => {
        heading.classList.add('z-content-navigation-target');
        registerHeaderLayout(heading);
        const link = document.createElement('a');
        // The storefront has a document <base>, so a bare #fragment would point to the homepage.
        const url = new URL(window.location.href);
        url.hash = headingId(heading, index);
        link.href = url.href;
        const label = document.createElement('span');
        label.setAttribute('data-nosnippet', '');
        label.textContent = heading.textContent.trim();
        link.append(label);
        const item = document.createElement('li');
        item.append(link);
        list.append(item);
        return link;
    });

    function markCurrent(id) {
        links.forEach((link) => {
            if (link.hash === `#${id}`) link.setAttribute('aria-current', 'location');
            else link.removeAttribute('aria-current');
        });
    }

    links.forEach((link, index) => {
        link.addEventListener('click', (event) => {
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return;
            }

            event.preventDefault();
            window.history.pushState(null, '', link.href);
            scrollToHeading(headings[index], reducedMotion.matches ? 'auto' : 'smooth');
            markCurrent(headings[index].id);
        });
    });

    const observer = new IntersectionObserver(
        (entries) => {
            const visible = entries
                .filter((entry) => entry.isIntersecting)
                .sort(
                    (first, second) => first.boundingClientRect.top - second.boundingClientRect.top,
                );
            if (visible[0]) markCurrent(visible[0].target.id);
        },
        { rootMargin: '-12% 0px -72% 0px' },
    );
    headings.forEach((heading) => observer.observe(heading));
    markCurrent(headings[0].id);
    navigation.hidden = false;

    const initialIndex = links.findIndex((link) => link.hash === window.location.hash);
    if (initialIndex >= 0) {
        window.requestAnimationFrame(() => {
            scrollToHeading(headings[initialIndex], 'auto');
            markCurrent(headings[initialIndex].id);
        });
    }

    if (toggle) {
        const mobileQuery = window.matchMedia('(max-width: 47.99rem)');
        const setExpanded = (expanded) => {
            toggle.setAttribute('aria-expanded', String(expanded));
            toggle.setAttribute('aria-disabled', String(!mobileQuery.matches));
            toggle.tabIndex = mobileQuery.matches ? 0 : -1;
            list.hidden = !expanded;
        };
        const updateLayout = () => setExpanded(!mobileQuery.matches);

        toggle.addEventListener('click', () => {
            if (!mobileQuery.matches) return;
            setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
        });
        mobileQuery.addEventListener('change', updateLayout);
        updateLayout();
    }
}
